/**
 * Boutique Margo' — preferiti senza app.
 *
 * I preferiti sono gli handle dei prodotti, salvati nel browser (localStorage). Nessun
 * account, nessuna app: restano sul dispositivo finché la cliente non li toglie.
 *
 * Ganci nel markup:
 *   [data-margo-preferito="<handle>"]   cuore (blocks/margo-preferito.liquid)
 *   [data-margo-preferito-testo]        etichetta del cuore in linea
 *   [data-margo-preferiti-conta]        contatore (header)
 *   [data-margo-preferiti-lista]        griglia della pagina preferiti (sections/margo-preferiti.liquid)
 *
 * Caricato come modulo: anche se ogni card lo richiama, il browser lo esegue una volta.
 */

const CHIAVE = 'margo-preferiti';
const EVENTO = 'margo:preferiti';

function leggi() {
  try {
    const valore = JSON.parse(localStorage.getItem(CHIAVE) || '[]');
    return Array.isArray(valore) ? valore.filter((h) => typeof h === 'string') : [];
  } catch {
    return [];
  }
}

function scrivi(handles) {
  try {
    localStorage.setItem(CHIAVE, JSON.stringify(handles));
  } catch {
    // Navigazione privata o spazio pieno: il cuore si aggiorna lo stesso per questa pagina.
  }
  document.dispatchEvent(new CustomEvent(EVENTO, { detail: { handles } }));
}

function aggiornaCuori(radice = document) {
  const handles = new Set(leggi());
  radice.querySelectorAll('[data-margo-preferito]').forEach((bottone) => {
    const attivo = handles.has(bottone.dataset.margoPreferito);
    const etichetta = attivo ? bottone.dataset.etichettaTogli : bottone.dataset.etichettaAggiungi;
    bottone.setAttribute('aria-pressed', String(attivo));
    if (etichetta) bottone.setAttribute('aria-label', etichetta);
    const testo = bottone.querySelector('[data-margo-preferito-testo]');
    if (testo && etichetta) testo.textContent = etichetta;
  });
  document.querySelectorAll('[data-margo-preferiti-conta]').forEach((el) => {
    el.textContent = handles.size ? String(handles.size) : '';
    el.hidden = handles.size === 0;
  });
}

function alterna(handle) {
  const handles = leggi();
  const i = handles.indexOf(handle);
  if (i === -1) handles.unshift(handle);
  else handles.splice(i, 1);
  scrivi(handles);
}

/* --- pagina dei preferiti ------------------------------------------------- */

const radice = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function prezzo(centesimi) {
  const valuta = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  return new Intl.NumberFormat(document.documentElement.lang || 'it', { style: 'currency', currency: valuta }).format(
    centesimi / 100
  );
}

function testo(tag, classe, contenuto) {
  const el = document.createElement(tag);
  el.className = classe;
  el.textContent = contenuto;
  return el;
}

// Stessa regola delle card del tema (snippets/margo-fondo-foto.liquid): cornice 2:3, e la
// foto piu' alta della cornice entra intera, con ai lati il colore dei suoi angoli.
const CORNICE = 2 / 3;

function fondo(prodotto, base) {
  const media = prodotto.media || [];
  const scelto = media.find((m) => m.preview_image && m.preview_image.src === prodotto.featured_image) || media[0];
  const foto = scelto && scelto.preview_image;
  if (!foto || !foto.aspect_ratio || foto.aspect_ratio >= CORNICE - 0.001) return null;

  const lato = ((1 - foto.aspect_ratio / CORNICE) / 2) * 100;
  const cw = Math.ceil(foto.width * 0.03);
  const ch = Math.ceil(foto.height * 0.02);
  const angolo = (x, y) => {
    const url = new URL(base, window.location.origin);
    const parametri = { crop: 'region', crop_left: x, crop_top: y, crop_width: cw, crop_height: ch, width: 1, height: 1 };
    for (const [nome, valore] of Object.entries(parametri)) url.searchParams.set(nome, String(valore));
    return `url("${url.toString()}")`;
  };

  const span = document.createElement('span');
  span.className = 'margo-fondo';
  span.setAttribute('aria-hidden', 'true');
  span.style.setProperty('--margo-lato', `${lato.toFixed(2)}%`);
  span.style.setProperty('--margo-sfuma', '0%');
  span.style.setProperty('--margo-alto-sx', angolo(0, 0));
  span.style.setProperty('--margo-alto-dx', angolo(foto.width - cw, 0));
  span.style.setProperty('--margo-basso-sx', angolo(0, foto.height - ch));
  span.style.setProperty('--margo-basso-dx', angolo(foto.width - cw, foto.height - ch));
  for (const verso of ['sx', 'dx']) {
    const banda = document.createElement('span');
    banda.className = `margo-fondo__lato margo-fondo__lato--${verso}`;
    span.append(banda);
  }
  return span;
}

function card(prodotto, lista) {
  const li = document.createElement('li');
  li.className = 'margo-preferiti__card';

  const link = document.createElement('a');
  link.className = 'margo-preferiti__link';
  link.href = prodotto.url;

  const media = document.createElement('div');
  media.className = 'margo-preferiti__media';
  if (prodotto.featured_image) {
    const img = document.createElement('img');
    const base = prodotto.featured_image.startsWith('//') ? `https:${prodotto.featured_image}` : prodotto.featured_image;
    const url = new URL(base, window.location.origin);
    url.searchParams.set('width', '700');
    img.src = url.toString();
    img.alt = prodotto.title;
    img.loading = 'lazy';
    img.width = 700;
    img.height = 1050;
    const banda = fondo(prodotto, base);
    if (banda) media.append(banda);
    media.append(img);
  }
  link.append(media);
  if (prodotto.vendor) link.append(testo('p', 'margo-marchio', prodotto.vendor));
  link.append(testo('h3', 'margo-preferiti__titolo', prodotto.title));

  const riga = document.createElement('p');
  riga.className = 'margo-preferiti__prezzo';
  riga.append(document.createTextNode(prezzo(prodotto.price)));
  if (prodotto.compare_at_price && prodotto.compare_at_price > prodotto.price) {
    riga.append(' ', testo('s', '', prezzo(prodotto.compare_at_price)));
  }
  if (!prodotto.available) riga.append(' · ', testo('span', '', lista.dataset.esaurito || ''));
  link.append(riga);
  li.append(link);

  const togli = document.createElement('button');
  togli.type = 'button';
  togli.className = 'margo-preferiti__togli';
  togli.textContent = lista.dataset.togli || '';
  togli.addEventListener('click', () => {
    alterna(prodotto.handle);
    li.remove();
    if (!lista.querySelector('li')) mostraVuoto(lista);
  });
  li.append(togli);
  return li;
}

function mostraVuoto(lista) {
  const vuoto = lista.parentElement.querySelector('[data-margo-preferiti-vuoto]');
  if (vuoto) vuoto.hidden = false;
  lista.hidden = true;
}

async function riempiLista(lista) {
  const handles = leggi();
  if (!handles.length) return mostraVuoto(lista);
  const risposte = await Promise.all(
    handles.map((h) =>
      fetch(`${radice()}products/${encodeURIComponent(h)}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
    )
  );
  const trovati = risposte.filter(Boolean);
  // Un prodotto tolto dal negozio sparisce anche dai preferiti, invece di restare un buco.
  if (trovati.length !== handles.length) scrivi(trovati.map((p) => p.handle));
  if (!trovati.length) return mostraVuoto(lista);
  lista.replaceChildren(...trovati.map((p) => card(p, lista)));
  lista.hidden = false;
}

/* --- avvio ------------------------------------------------------------------ */

document.addEventListener('click', (evento) => {
  const bottone = evento.target.closest('[data-margo-preferito]');
  if (!bottone) return;
  evento.preventDefault();
  alterna(bottone.dataset.margoPreferito);
});

document.addEventListener(EVENTO, () => aggiornaCuori());
window.addEventListener('storage', (evento) => {
  if (evento.key === CHIAVE) aggiornaCuori();
});

// Card aggiunte dopo il caricamento (scroll infinito, raccomandazioni, ricerca).
let attesa = 0;
new MutationObserver(() => {
  cancelAnimationFrame(attesa);
  attesa = requestAnimationFrame(() => aggiornaCuori());
}).observe(document.body, { childList: true, subtree: true });

aggiornaCuori();
document.querySelectorAll('[data-margo-preferiti-lista]').forEach((lista) => riempiLista(lista));
